import { useEffect, useState } from "react";

import dayjs from "dayjs";

import { BsCheck, BsEye, BsXCircle } from "react-icons/bs";
import { MdBlock } from "react-icons/md";
import { toast } from "react-toastify";
import { FaBook } from "react-icons/fa";
import Table from "react-bootstrap/Table";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";

import { getCityLicenseStatus } from "@/utils/cityLicenseStatus";
import { Account } from "@/@types/app/Account";
import { CityLicense } from "@/@types/app/CityLicense";
import { User } from "@/@types/app/user";
import {
	acceptCityLicense,
	getCityLicensesByCity,
	invalidateCityLicense,
	rejectCityLicense,
} from "@/services/app/cityLicenseService";
import { getAllModules, ModuleDTO } from "@/services/app/moduleService";
import { ModuleEnum } from "@/utils/enums/ModuleEnum";
import { Option } from "@/@types/generics/Option";

import { BaseModalProps } from "../BaseModal";
import { ModalLicense } from "./ModalLicense";
import { NoRecordsFeedback } from "../NoRecordsFeedback";
import UserInformationModal from "./UserInformationModal";
import { ModuleSelect } from "../ModuleSelect";

type Props = BaseModalProps & {
	selectedCity: Account;
};

export function ModalLicenseManagement({ selectedCity, ...rest }: Props) {
	const [cityLicenses, setCityLicenses] = useState<CityLicense[]>([]);
	const [modules, setModules] = useState<ModuleDTO[]>([]);
	const [userSelectedLicense, setSelectedUserLicense] = useState<User>();
	const [selectedOptionModule, setSelectedOptionModule] = useState<Option<ModuleEnum>>({
		label: "Esus Aps",
		value: ModuleEnum.ESUS_APS,
	} as Option<ModuleEnum>);

	async function handleGetCityLicenseHistory() {
		if (!selectedCity.ibgeCode) return;
		setCityLicenses(await getCityLicensesByCity(selectedCity.ibgeCode));
	}

	async function handleInvalidateCityLicense(ibgeCode?: string, module?: ModuleEnum) {
		if (!ibgeCode) return;
		const isInvalidated = await invalidateCityLicense(ibgeCode, module);
		if (isInvalidated) {
			toast.success(`Licença revogada com sucesso`, { autoClose: 3000 });
			handleGetCityLicenseHistory();
		} else {
			toast.error(`Erro ao revogar licença`, { autoClose: 3000 });
		}
	}

	async function handleAcceptCityLicense(ibgeCode?: string, module?: ModuleEnum) {
		if (!ibgeCode) return;
		const isAccept = await acceptCityLicense(ibgeCode, module);
		if (isAccept) {
			toast.success(`Solicitação aceita com sucesso`, { autoClose: 3000 });
			handleGetCityLicenseHistory();
		} else {
			toast.error(`Erro ao aceitar solicitação`, { autoClose: 3000 });
		}
	}

	async function handleRejectCityLicense(ibgeCode?: string, module?: ModuleEnum) {
		if (!ibgeCode) return;
		const isReject = await rejectCityLicense(ibgeCode, module);
		if (isReject) {
			toast.success(`Solicitação rejeitada com sucesso`, { autoClose: 3000 });
			handleGetCityLicenseHistory();
		} else {
			toast.error(`Erro ao rejeitar solicitação`, { autoClose: 3000 });
		}
	}

	async function handleAllModules() {
		setModules(await getAllModules());
	}

	const [showModalLicense, setShowModalLicense] = useState(false);
	const handleShowModalLicense = () => {
		setShowModalLicense(true);
	};
	const handleCloseModalLicense = () => {
		setShowModalLicense(false);
		handleGetCityLicenseHistory();
	};

	const [showModalInfo, setShowModalInfo] = useState(false);

	const handleCloseModalInfo = () => setShowModalInfo(false);
	const handleShowModalInfo = () => setShowModalInfo(true);

	useEffect(() => {
		if (selectedCity?.ibgeCode) {
			handleGetCityLicenseHistory();
		}
		handleAllModules();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedCity]);

	return (
		<Modal size="xl" scrollable {...rest}>
			<ModalLicense
				show={showModalLicense}
				onHide={handleCloseModalLicense}
				city={selectedCity}
				modules={modules}
			/>
			<UserInformationModal
				show={showModalInfo}
				onHide={handleCloseModalInfo}
				userSelectedLicense={userSelectedLicense}
			/>
			<Modal.Header closeButton>
				<Modal.Title>
					Histórico de Licenças
					<label className="d-flex text-primary fs-6 ">
						{[selectedCity?.locale, selectedCity?.federalUnit?.abbreviation]
							.filter((value) => value && value.trim().length > 0)
							.join(" - ")}
					</label>
				</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<Row className="px-3 align-items-end justify-content-between">
					<Col xs={8} md={6} lg={5}>
						<ModuleSelect
							id="module-select"
							className="fs-8"
							noDefaultOption={true}
							modules={modules}
							value={selectedOptionModule}
							onChange={(newValue) => {
								setSelectedOptionModule(newValue as Option<ModuleEnum>);
							}}
						/>
					</Col>
					<Col xs={4} md={3} className="text-end">
						<Button
							variant="secondary"
							onClick={handleShowModalLicense}
							className="py-2"
						>
							Nova Licença
						</Button>
					</Col>
				</Row>

				<Table striped bordered hover className="m-3">
					<thead>
						<tr>
							<th className="text-center">
								<strong>DATA DE INICIO</strong>
							</th>
							<th className="text-center">
								<strong>DATA DE EXPIRAÇÃO</strong>
							</th>
							<th className="text-center">
								<strong>CPF SOLICITANTE</strong>
							</th>
							<th className="text-center">
								<strong>NOME SOLICITANTE</strong>
							</th>
							<th className="text-center">
								<strong>CARGO</strong>
							</th>
							<th className="text-center">
								<strong>TIPO DE LICENÇA</strong>
							</th>
							<th className="text-center">
								<strong>AÇÕES</strong>
							</th>
						</tr>
					</thead>

					<tbody>
						{cityLicenses
							.filter(
								(cityLicense) =>
									cityLicense.license?.moduleId === selectedOptionModule?.value
							)
							.map((cityLicense, index) => (
								<tr key={index}>
									<td className="text-center">
										{cityLicense.startDate
											? dayjs(cityLicense.startDate).format(
													"DD/MM/YYYY HH:mm"
											  )
											: "-"}
									</td>
									<td className="text-center">
										{cityLicense.endDate
											? dayjs(cityLicense.endDate).format("DD/MM/YYYY HH:mm")
											: "-"}
									</td>
									<td className="text-center">{cityLicense.user?.identifier}</td>
									<td className="text-center">{cityLicense.user?.fullName}</td>
									<td className="text-center">{cityLicense.user?.occupation}</td>
									<td className="text-center">{cityLicense.license?.name}</td>
									<td>
										{index === 0 &&
											getCityLicenseStatus(cityLicense) &&
											(getCityLicenseStatus(cityLicense) === "pendente" ? (
												<div className="d-flex">
													<Button
														className="me-2"
														title="Aprovar"
														variant="success"
														onClick={() =>
															handleAcceptCityLicense(
																cityLicense?.city?.ibgeCode,
																selectedOptionModule?.value
															)
														}
													>
														<BsCheck className="fs-3" />
													</Button>
													<Button
														className="me-2"
														title="Rejeitar"
														variant="danger"
														onClick={() =>
															handleRejectCityLicense(
																cityLicense?.city?.ibgeCode,
																selectedOptionModule.value
															)
														}
													>
														<BsXCircle className="fs-3" />
													</Button>
													<Button
														title="Informações"
														onClick={() => {
															handleShowModalInfo();
															setSelectedUserLicense(
																cityLicense.user
															);
														}}
													>
														<BsEye />
													</Button>
												</div>
											) : (
												getCityLicenseStatus(cityLicense) === "ativo" && (
													<div className="d-flex">
														<Button
															className="me-2"
															title="Invalidar licença"
															variant="danger"
															onClick={() =>
																handleInvalidateCityLicense(
																	cityLicense?.city?.ibgeCode,
																	selectedOptionModule.value
																)
															}
														>
															<MdBlock className="fs-3" />
														</Button>
														<Button
															title="Informações"
															onClick={() => {
																handleShowModalInfo();
																setSelectedUserLicense(
																	cityLicense.user
																);
															}}
														>
															<BsEye className="fs-3" />
														</Button>
													</div>
												)
											))}
									</td>
								</tr>
							))}
					</tbody>
				</Table>
				{cityLicenses.length < 1 && (
					<div className="d-flex justify-content-center">
						<NoRecordsFeedback icon={<FaBook />} message={"Não Existe Licenças"} />
					</div>
				)}
			</Modal.Body>
		</Modal>
	);
}
